// Notifications data layer: the member's own list (with the unread count) and read marks. The server
// scopes everything to the signed-in member; nothing here can address anyone else's notifications.
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { post, get } from '../../lib/api.js';

export interface AppNotification {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  url: string | null;
  leadId: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationList {
  items: AppNotification[];
  unreadCount: number;
  nextCursor: string | null;
}

export const NOTIFICATIONS_KEY = ['notifications', 'list'] as const;

export function useNotifications(enabled = true) {
  return useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: () => get<NotificationList>('/notifications?limit=30'),
    enabled,
    staleTime: 30_000,
    // A safety net for a missed realtime message (a phone that slept): the count catches up within a minute.
    refetchInterval: 60_000,
  });
}

/** Puts a notification that just arrived at the top of the cached list, before the refetch lands. */
export function prependNotification(qc: QueryClient, n: AppNotification): void {
  qc.setQueryData<NotificationList>(NOTIFICATIONS_KEY, (old) => {
    if (!old) return old;
    if (old.items.some((i) => i.id === n.id)) return old;
    return { ...old, items: [n, ...old.items], unreadCount: old.unreadCount + (n.readAt ? 0 : 1) };
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post<{ unreadCount: number }>(`/notifications/${id}/read`),
    onMutate: (id) => {
      qc.setQueryData<NotificationList>(NOTIFICATIONS_KEY, (old) => {
        if (!old) return old;
        const target = old.items.find((i) => i.id === id);
        if (!target || target.readAt) return old;
        return {
          ...old,
          unreadCount: Math.max(0, old.unreadCount - 1),
          items: old.items.map((i) => (i.id === id ? { ...i, readAt: new Date().toISOString() } : i)),
        };
      });
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<{ unreadCount: number }>('/notifications/read-all'),
    onMutate: () => {
      qc.setQueryData<NotificationList>(NOTIFICATIONS_KEY, (old) =>
        old
          ? {
              ...old,
              unreadCount: 0,
              items: old.items.map((i) => (i.readAt ? i : { ...i, readAt: new Date().toISOString() })),
            }
          : old,
      );
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
}

/** "just now", "5 min ago", "3 h ago", "2 d ago", then the date. */
export function timeAgo(iso: string, now = Date.now()): string {
  const secs = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}
